import {
  EventType,
  LogLevel,
  PublicClientApplication,
} from '@azure/msal-browser';
import type {
  AuthenticationResult,
  Configuration,
  EventMessage,
  PopupRequest,
  AccountInfo,
} from '@azure/msal-browser';
import { getEnv } from '@/utils/env';

// Create auth config dynamically to ensure runtime values are used
export const getAuthConfig = (): Configuration => {
  const client_id = getEnv('VITE_CLIENT_ID');
  const tenant_id = getEnv('VITE_TENANT_ID');
  const redirect_url =
    getEnv('VITE_REDIRECT_URI') || `${window.location.origin}`;
  const post_logout_redirect_url =
    getEnv('VITE_POST_LOGOUT_REDIRECT_URI') || window.location.origin;

  if (!client_id || !tenant_id) {
    throw new Error(
      'Azure AD configuration missing: VITE_CLIENT_ID and VITE_TENANT_ID are required'
    );
  }

  return {
    auth: {
      clientId: client_id,
      authority: `https://login.microsoftonline.com/${tenant_id}`,
      redirectUri: redirect_url,
      postLogoutRedirectUri: post_logout_redirect_url,
      navigateToLoginRequestUrl: false,
    },
    cache: {
      cacheLocation: 'localStorage',
      storeAuthStateInCookie: true,
    },
    system: {
      loggerOptions: {
        loggerCallback: (
          level: LogLevel,
          message: string,
          containsPii: boolean
        ) => {
          if (containsPii) {
            return;
          }
          switch (level) {
            case LogLevel.Error:
              console.error(message);
              return;
            case LogLevel.Info:
              return;
            case LogLevel.Verbose:
              return;
            case LogLevel.Warning:
              console.warn(message);
              return;
            default:
              return;
          }
        },
        logLevel: LogLevel.Warning,
      },
    },
  };
};

let msalInstance: PublicClientApplication | null = null;
let isInitializing = false;
let initializePromise: Promise<PublicClientApplication | null> | null = null;

/**
 * Check if MSAL is properly configured with required credentials
 */
export const isMsalConfigured = (): boolean => {
  try {
    const client_id = getEnv('VITE_CLIENT_ID');
    const tenant_id = getEnv('VITE_TENANT_ID');
    return !!(client_id && tenant_id);
  } catch {
    return false;
  }
};

export const initializeMsal =
  async (): Promise<PublicClientApplication | null> => {
    // Check if MSAL is configured before attempting initialization
    if (!isMsalConfigured()) {
      console.warn(
        'MSAL not configured: VITE_CLIENT_ID and VITE_TENANT_ID are required. Authentication will not work until configured.'
      );
      return null;
    }

    if (msalInstance && !isInitializing) {
      return msalInstance;
    }

    if (initializePromise) {
      return initializePromise;
    }

    isInitializing = true;
    initializePromise = new Promise(resolve => {
      try {
        const authConfig = getAuthConfig();
        const instance = new PublicClientApplication(authConfig);

        instance.addEventCallback((event: EventMessage) => {
          if (event.eventType === EventType.LOGIN_SUCCESS && event.payload) {
            const payload = event.payload as AuthenticationResult;
            if (payload.account) {
              instance.setActiveAccount(payload.account);
            }
          }
        });

        instance
          .initialize()
          .then(() => {
            instance
              .handleRedirectPromise()
              .then(() => {
                msalInstance = instance;
                isInitializing = false;
                resolve(msalInstance);
              })
              .catch((error: unknown) => {
                console.error('Handle redirect promise error:', error);
                msalInstance = instance;
                isInitializing = false;
                resolve(msalInstance);
              });
          })
          .catch((error: unknown) => {
            isInitializing = false;
            initializePromise = null;
            console.error('MSAL initialization failed:', error);
            // Resolve with null instead of rejecting to prevent app crash
            resolve(null);
          });
      } catch (error: unknown) {
        isInitializing = false;
        initializePromise = null;
        console.error('MSAL configuration error:', error);
        // Resolve with null instead of rejecting to prevent app crash
        resolve(null);
      }
    });

    return initializePromise;
  };

export const getMsalInstance =
  async (): Promise<PublicClientApplication | null> => {
    if (!isMsalConfigured()) {
      return null;
    }

    if (!msalInstance || isInitializing) {
      return initializeMsal();
    }
    return msalInstance;
  };

// Login request for SSO - no prompt to allow automatic SSO
export const loginRequest: PopupRequest = {
  scopes: ['User.Read'],
  prompt: 'select_account',
};

export const tokenRequest: PopupRequest = {
  scopes: ['User.Read'],
};

// Helper to get current account
export const getCurrentAccount = async (): Promise<AccountInfo | null> => {
  const instance = await getMsalInstance();
  if (!instance) {
    return null;
  }
  return instance.getActiveAccount();
};

// Helper to get account info for Redux
export const getAccountInfo = (account: AccountInfo | null) => {
  if (!account) return null;

  return {
    id: account.homeAccountId,
    email: account.username || '',
    name: account.name || account.username || '',
  };
};

/**
 * Get valid idToken and accessToken, only refreshing if necessary
 * Checks token expiration first before calling MSAL
 * This should be called before making API requests
 * @returns Object with idToken (for Authorization header) and accessToken (for x-graph-token header)
 */
export const getIdToken = async (
  currentToken?: string | null
): Promise<{ token: string | null; graphToken: string | null }> => {
  try {
    const { isTokenExpired } = await import('@/utils/token');
    const { STORAGE_KEYS } = await import('@/constants');

    // Get both tokens from localStorage
    const storedIdToken =
      currentToken || localStorage.getItem(STORAGE_KEYS.TOKEN);
    const storedGraphToken = localStorage.getItem(STORAGE_KEYS.GRAPH_TOKEN);

    // Check if both tokens are valid (not expired)
    const idTokenValid = storedIdToken && !isTokenExpired(storedIdToken);
    const graphTokenValid =
      storedGraphToken && !isTokenExpired(storedGraphToken);

    // If both tokens are valid, return them without making any network calls
    if (idTokenValid && graphTokenValid) {
      return {
        token: storedIdToken,
        graphToken: storedGraphToken,
      };
    }

    // If we have a valid idToken but expired/missing graphToken, try to get only graphToken
    if (idTokenValid && !graphTokenValid) {
      const instance = await getMsalInstance();
      if (instance) {
        const account = await getCurrentAccount();
        if (account) {
          try {
            // Try to get accessToken silently from cache
            const tokenResponse = await instance.acquireTokenSilent({
              ...tokenRequest,
              account: account,
            });
            // Cache the new graphToken
            if (tokenResponse.accessToken) {
              localStorage.setItem(
                STORAGE_KEYS.GRAPH_TOKEN,
                tokenResponse.accessToken
              );
            }
            return {
              token: storedIdToken, // Use cached idToken
              graphToken: tokenResponse.accessToken, // Get fresh accessToken
            };
          } catch {
            // If we can't get accessToken, return what we have
            return {
              token: storedIdToken,
              graphToken: null,
            };
          }
        }
      }
      // Fallback: return cached idToken, accessToken will be null
      return {
        token: storedIdToken,
        graphToken: null,
      };
    }

    // One or both tokens are expired or missing, get fresh tokens from MSAL
    const instance = await getMsalInstance();

    if (!instance) {
      console.warn('MSAL instance not available');
      return { token: null, graphToken: null };
    }

    const account = await getCurrentAccount();

    if (!account) {
      console.warn('No active account found');
      return { token: null, graphToken: null };
    }
    // Try to acquire token silently (MSAL will use cache if valid, refresh if needed)
    try {
      const tokenResponse = await instance.ssoSilent({
        ...tokenRequest,
        account: account,
      });

      // Cache both tokens
      if (tokenResponse.idToken) {
        localStorage.setItem(STORAGE_KEYS.TOKEN, tokenResponse.idToken);
      }
      if (tokenResponse.accessToken) {
        localStorage.setItem(
          STORAGE_KEYS.GRAPH_TOKEN,
          tokenResponse.accessToken
        );
      }

      return {
        token: tokenResponse.idToken,
        graphToken: tokenResponse.accessToken,
      };
    } catch (silentError) {
      // If silent acquisition fails, try interactive (popup)
      console.warn(
        'Silent token acquisition failed, trying interactive:',
        silentError
      );
      try {
        const tokenResponse = await instance.acquireTokenPopup({
          ...tokenRequest,
          account: account,
        });

        // Cache both tokens
        if (tokenResponse.idToken) {
          localStorage.setItem(STORAGE_KEYS.TOKEN, tokenResponse.idToken);
        }
        if (tokenResponse.accessToken) {
          localStorage.setItem(
            STORAGE_KEYS.GRAPH_TOKEN,
            tokenResponse.accessToken
          );
        }

        return {
          token: tokenResponse.idToken,
          graphToken: tokenResponse.accessToken,
        };
      } catch (popupError) {
        console.error('Failed to acquire token:', popupError);
        return { token: null, graphToken: null };
      }
    }
  } catch (error) {
    console.error('Error getting tokens:', error);
    return { token: null, graphToken: null };
  }
};
